import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Heart, Check, XCircle, CalendarClock, Radio, Sparkles, ShieldCheck, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Badge, STATUS_META, FilterTabs, timeAgo } from "./engShared";

const DTYPE_LABEL = {
  love: "Love", birthday: "Birthday", anniversary: "Anniversary",
  congratulations: "Congrats", memorial: "Memorial", shoutout: "Shout-Out",
  song_dedication: "Song Dedication",
};

export default function EngDedications() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState("all");
  const [busy, setBusy] = useState("");

  const load = () => api.get(`/engagement/dedications?status=${status}`)
    .then((r) => { setItems(r.data.items || []); setCounts(r.data.counts || {}); });
  useEffect(() => { load(); }, [status]); // eslint-disable-line

  const act = async (id, action, extra = {}) => {
    try {
      await api.post(`/engagement/dedications/${id}/action`, { action, ...extra });
      toast.success("Done");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const aiWording = async (id) => {
    setBusy(id);
    try {
      await api.post(`/engagement/dedications/${id}/ai-wording`);
      toast.success("On-air wording drafted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "AI wording failed"); }
    setBusy("");
  };

  return (
    <div className="space-y-4" data-testid="eng-dedications-page">
      <div>
        <h1 className="font-display text-2xl font-bold">Dedications</h1>
        <p className="text-sm text-ink-secondary">Listener shout-outs, birthdays and memorials — memorial wording always gets a human review first.</p>
      </div>
      <FilterTabs active={status} onChange={setStatus} counts={counts}
        tabs={[["all", "All"], ["pending", "Pending"], ["approved", "Approved"], ["scheduled", "Scheduled"], ["aired", "Aired"], ["rejected", "Rejected"]]} />

      <div className="space-y-3">
        {!items.length && <div className="rounded-xl border border-line bg-bg-surface p-8 text-center text-sm text-ink-muted" data-testid="eng-dedications-empty">No dedications in this view.</div>}
        {items.map((d) => (
          <div key={d.dedication_id} className="rounded-xl border border-line bg-bg-surface p-4" data-testid={`eng-dedication-${d.dedication_id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <Heart className="h-3.5 w-3.5 text-pink-300" />
              <span className="text-sm font-semibold">{d.sender_name || "Listener"} → {d.recipient_name || "—"}</span>
              <span className="rounded-full border border-pink-400/40 bg-pink-400/10 text-pink-300 px-2 py-0.5 text-[10px] font-mono uppercase">{DTYPE_LABEL[d.dtype] || d.dtype}</span>
              <Badge meta={STATUS_META[d.status]} />
              {d.dtype === "memorial" && d.review_status !== "reviewed" && (
                <span className="rounded-full border border-rose-500/40 bg-rose-500/10 text-rose-300 px-2 py-0.5 text-[10px] font-mono uppercase" data-testid={`eng-ded-needs-review-${d.dedication_id}`}>Needs manual review</span>
              )}
              <span className="text-[10px] text-ink-muted ml-auto">{timeAgo(d.created_at)}</span>
            </div>
            <div className="text-xs text-ink-secondary mt-2 whitespace-pre-wrap">{d.message}</div>
            {d.song && <div className="text-[11px] text-brand-cyan mt-1">♪ {d.song}</div>}
            {d.ai_wording && (
              <div className="mt-2 rounded-lg border border-brand-amber/30 bg-brand-amber/5 p-2.5">
                <div className="text-[10px] font-mono uppercase text-brand-amber mb-1">On-air wording</div>
                <div className="text-xs whitespace-pre-wrap">{d.ai_wording}</div>
              </div>
            )}
            <div className="flex flex-wrap gap-1.5 mt-3">
              {d.dtype === "memorial" && d.review_status !== "reviewed" && (
                <button onClick={() => act(d.dedication_id, "mark_reviewed")} data-testid={`eng-ded-review-${d.dedication_id}`}
                  className="inline-flex items-center gap-1 rounded-full border border-rose-400/50 text-rose-300 px-2.5 py-1 text-[11px]"><ShieldCheck className="h-3 w-3" /> Mark reviewed</button>
              )}
              <button onClick={() => act(d.dedication_id, "approve")} data-testid={`eng-ded-approve-${d.dedication_id}`}
                className="inline-flex items-center gap-1 rounded-full border border-emerald-500/50 text-emerald-300 px-2.5 py-1 text-[11px]"><Check className="h-3 w-3" /> Approve</button>
              <button onClick={() => act(d.dedication_id, "reject")} data-testid={`eng-ded-reject-${d.dedication_id}`}
                className="inline-flex items-center gap-1 rounded-full border border-rose-500/50 text-rose-300 px-2.5 py-1 text-[11px]"><XCircle className="h-3 w-3" /> Reject</button>
              <button onClick={() => act(d.dedication_id, "schedule")} data-testid={`eng-ded-schedule-${d.dedication_id}`}
                className="inline-flex items-center gap-1 rounded-full border border-sky-500/50 text-sky-300 px-2.5 py-1 text-[11px]"><CalendarClock className="h-3 w-3" /> Schedule</button>
              <button onClick={() => act(d.dedication_id, "aired")} data-testid={`eng-ded-aired-${d.dedication_id}`}
                className="inline-flex items-center gap-1 rounded-full border border-line px-2.5 py-1 text-[11px]"><Radio className="h-3 w-3" /> Mark aired</button>
              <button onClick={() => aiWording(d.dedication_id)} disabled={busy === d.dedication_id} data-testid={`eng-ded-ai-${d.dedication_id}`}
                className="inline-flex items-center gap-1 rounded-full border border-brand-amber/50 text-brand-amber px-2.5 py-1 text-[11px] disabled:opacity-50">
                {busy === d.dedication_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} AI wording
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
